import { useEffect, useRef, useState } from 'react';

import { withErrorBoundary } from '../ErrorBoundary';

import {
  StyledCell,
  StyledCellContents,
  StyledHeadingCell,
  StyledRow,
  StyledTable,
  StyledWrapper,
  StyledWrapperInner,
} from './Styled';

export interface TableProps {
  headings: string[];
  rows: string[][];
  fixFirstColumn?: boolean;
}

const minWidth = 125;

const Table = ({ headings, rows, fixFirstColumn }: TableProps) => {
  const scrollableRef = useRef<HTMLDivElement | null>(null);
  const [scrollPositions, setScroll] = useState({ isScrolledToLeft: true, isScrolledToRight: true });
  const [minCellWidth, setMinCellWidth] = useState(minWidth);
  const [scrollableWidth, setScrollableWidth] = useState(0);

  const scrollHandler = () => {
    if (scrollableRef.current) {
      const isScrolledToLeft = scrollableRef.current.scrollLeft === 0;
      const isScrolledToRight =
        scrollableRef.current.scrollLeft === scrollableRef.current.scrollWidth - scrollableRef.current.offsetWidth;
      setScroll({ isScrolledToLeft, isScrolledToRight });
    }
  };

  useEffect(() => {
    scrollHandler();
    if (scrollableRef.current && scrollableWidth) {
      setMinCellWidth(Math.max(minWidth, scrollableWidth / 6 - 1));
    }
  }, [scrollableRef, scrollableWidth]);

  useEffect(() => {
    const currentRef = scrollableRef.current;
    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(entries => {
            setScrollableWidth(entries[0] && entries[0].contentRect && entries[0].contentRect.width);
          })
        : null;

    if (ro && currentRef) {
      ro.observe(currentRef);
    }

    return () => {
      if (ro && currentRef) {
        ro.unobserve(currentRef);
      }
    };
  }, [scrollableRef]);

  return (
    <StyledWrapper>
      <StyledWrapperInner
        ref={scrollableRef}
        fixFirstColumn={fixFirstColumn}
        isScrolledToLeft={scrollPositions.isScrolledToLeft}
        isScrolledToRight={scrollPositions.isScrolledToRight}
        onScroll={scrollHandler}
      >
        <StyledTable cellPadding={0} cellSpacing={0} fixFirstColumn={fixFirstColumn}>
          <thead>
            <StyledRow>
              {headings.map(heading => (
                <StyledHeadingCell key={heading}>
                  <StyledCellContents style={{ minWidth: minCellWidth }}>{heading}</StyledCellContents>
                </StyledHeadingCell>
              ))}
            </StyledRow>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <StyledRow key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <StyledCell key={cellIndex}>
                    <StyledCellContents style={{ minWidth: minCellWidth }}>{cell}</StyledCellContents>
                  </StyledCell>
                ))}
              </StyledRow>
            ))}
          </tbody>
        </StyledTable>
      </StyledWrapperInner>
    </StyledWrapper>
  );
};

export default withErrorBoundary(Table, { componentName: 'Table' });
